/**
 * Transfers between locations.
 *
 * A batch moves with its identity: the same batch number, expiry and cost
 * arrive as left. Dispatched stock is in transit — owned, valued, on no
 * shelf — until the destination records what arrived, once. A shortfall is
 * never absorbed: somebody who may approve stock decides it.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { ArrowLeftRight, Plus } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { useWorkingLocation } from '../../lib/locationContext';
import { dateTime } from '../../lib/format';
import { Button, Card, CardHeader, EmptyState, ErrorState, Input, Modal, Select, Spinner, Textarea } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { newKey, usePharmacy, usePharmacyWrite, type BatchRow, type ListOf } from './data';
import { ErrorLine, ExpiryBadge, StatusBadge, Table, Td } from './parts';

interface TransferRow {
  id: string;
  reference: string;
  from_branch_name: string | null;
  to_branch_name: string | null;
  status: string;
  dispatched_at: string | null;
  received_at: string | null;
  lines: number;
}

interface TransferLine {
  id: string;
  productId: string;
  productName: string;
  batchId: string;
  batchNumber: string;
  expiryDate: string;
  quantity: number;
  receivedQuantity: number | null;
  discrepancyReason: string | null;
  resolution: string | null;
}

interface Transfer {
  id: string;
  reference: string;
  fromBranchId: string;
  fromBranchName: string;
  toBranchId: string;
  toBranchName: string;
  status: string;
  note: string | null;
  dispatchedAt: string | null;
  dispatchedBy: string | null;
  receivedAt: string | null;
  receivedBy: string | null;
  reconciledAt: string | null;
  reconciliationNote: string | null;
  lines: TransferLine[];
}

export function TransfersPage(): ReactNode {
  const { can, business } = useAuth();
  const tz = business?.timezone ?? 'UTC';
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const list = usePharmacy<ListOf<TransferRow>>('/transfers', {}, can('pharmacy:transfers'));

  if (!can('pharmacy:transfers')) {
    return <EmptyState title="Transfers need the transfers permission" />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Transfers"
        description="Batches moved between locations — dispatched, in transit, received and reconciled"
        action={
          <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
            New transfer
          </Button>
        }
      />
      <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
        Batch number, expiry date and cost travel with the stock. Until received, it is counted as in transit — not on
        either shelf, and not sellable.
      </p>
      <Card padded={false}>
        {list.isError ? (
          <ErrorState message="Could not load transfers." onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Spinner label="Loading transfers" />
        ) : list.data.data.length === 0 ? (
          <EmptyState icon={<ArrowLeftRight className="size-8" />} title="No transfers yet" />
        ) : (
          <Table head={['Reference', 'From', 'To', 'Lines', 'Dispatched', 'Received', 'Status']} minWidth={720}>
            {list.data.data.map((t) => (
              <tr key={t.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen(t.id)}>
                <Td className="font-medium text-brand-700">{t.reference}</Td>
                <Td>{t.from_branch_name ?? '—'}</Td>
                <Td>{t.to_branch_name ?? '—'}</Td>
                <Td className="tabular-nums">{t.lines}</Td>
                <Td className="whitespace-nowrap">{t.dispatched_at ? dateTime(t.dispatched_at, tz) : '—'}</Td>
                <Td className="whitespace-nowrap">{t.received_at ? dateTime(t.received_at, tz) : '—'}</Td>
                <Td>
                  <StatusBadge status={t.status === 'dispatched' ? 'dispatched' : t.status} />
                  {t.status === 'dispatched' && <span className="ml-1 text-xs text-slate-500">in transit</span>}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
      <NewTransferModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false);
          setOpen(id);
        }}
      />
      {open && <TransferDetail id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function NewTransferModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }): ReactNode {
  const { locations, branchId, defaultBranchId } = useWorkingLocation();
  const [from, setFrom] = useState(branchId ?? defaultBranchId ?? '');
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [key, setKey] = useState(newKey);
  const batches = usePharmacy<ListOf<BatchRow>>('/batches', { branchId: from }, open && Boolean(from));
  const create = usePharmacyWrite<Record<string, unknown>, Transfer>('post', '/transfers');

  const rows = (batches.data?.data ?? []).filter((b) => b.onHand > 0);
  const chosen = Object.entries(quantities).filter(([, q]) => q !== '' && Number(q) > 0);
  const invalid = chosen.some(([id, q]) => {
    const b = rows.find((r) => r.batchId === id);
    return !b || !/^\d+$/.test(q) || Number(q) > b.onHand;
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    create.mutate(
      { fromBranchId: from, toBranchId: to, note: note || null, lines: chosen.map(([batchId, q]) => ({ batchId, quantity: Number(q) })), idempotencyKey: key },
      {
        onSuccess: (t) => {
          setQuantities({});
          setKey(newKey());
          onCreated(t.id);
        },
      },
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="New transfer" size="lg">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label="From"
            required
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setQuantities({});
            }}
            placeholder="Choose a location"
            options={locations.map((l) => ({ value: l.id, label: l.name }))}
          />
          <Select
            label="To"
            required
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="Choose a location"
            options={locations.filter((l) => l.id !== from).map((l) => ({ value: l.id, label: l.name }))}
          />
        </div>
        {!from ? null : !batches.data ? (
          <Spinner label="Loading batches" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500">Nothing on hand at this location.</p>
        ) : (
          <Table head={['Item', 'Batch', 'Expiry', 'On hand', 'Send']} minWidth={560}>
            {rows.map((b) => (
              <tr key={b.batchId} className={b.held ? 'bg-slate-50 text-slate-400' : undefined}>
                <Td className="font-medium">{b.productName}</Td>
                <Td>{b.batchNumber}</Td>
                <Td>
                  <ExpiryBadge expiryDate={b.expiryDate} expired={b.expired} nearExpiry={b.nearExpiry} />
                </Td>
                <Td className="tabular-nums">{b.onHand}</Td>
                <Td>
                  {b.held ? (
                    <span className="text-xs">On {b.holdKinds.join('/')} hold — decide the hold before moving it</span>
                  ) : (
                    <Input
                      aria-label={`Quantity of batch ${b.batchNumber} to send`}
                      type="number"
                      min={0}
                      max={b.onHand}
                      step={1}
                      className="w-24"
                      value={quantities[b.batchId] ?? ''}
                      onChange={(e) => setQuantities({ ...quantities, [b.batchId]: e.target.value })}
                    />
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        )}
        <Textarea label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
        {invalid && <p className="text-sm text-red-700">Send whole units, no more than each batch holds here.</p>}
        <ErrorLine error={create.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending} disabled={!from || !to || chosen.length === 0 || invalid}>
            Dispatch
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function TransferDetail({ id, onClose }: { id: string; onClose: () => void }): ReactNode {
  const { can, business } = useAuth();
  const tz = business?.timezone ?? 'UTC';
  const transfer = usePharmacy<Transfer>(`/transfers/${id}`);
  const t = transfer.data;

  return (
    <Modal open onClose={onClose} title={t ? `${t.reference} · ${t.fromBranchName} → ${t.toBranchName}` : 'Transfer'} size="lg">
      {!t ? (
        <Spinner label="Loading transfer" />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <StatusBadge status={t.status} />
            {t.dispatchedAt && <span className="text-slate-500">Dispatched {dateTime(t.dispatchedAt, tz)}{t.dispatchedBy ? ` by ${t.dispatchedBy}` : ''}</span>}
            {t.receivedAt && <span className="text-slate-500">· Received {dateTime(t.receivedAt, tz)}{t.receivedBy ? ` by ${t.receivedBy}` : ''}</span>}
          </div>
          {t.status === 'dispatched' && <p className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-800">In transit — on neither shelf until received.</p>}
          {t.status === 'dispatched' ? (
            <ReceiveForm transfer={t} />
          ) : (
            <Table head={['Item', 'Batch', 'Expiry', 'Sent', 'Received', 'Short', 'Reason / resolution']} minWidth={640}>
              {t.lines.map((l) => (
                <tr key={l.id}>
                  <Td className="font-medium">{l.productName}</Td>
                  <Td>{l.batchNumber}</Td>
                  <Td>{l.expiryDate}</Td>
                  <Td className="tabular-nums">{l.quantity}</Td>
                  <Td className="tabular-nums">{l.receivedQuantity ?? '—'}</Td>
                  <Td className="tabular-nums">{l.receivedQuantity === null ? '—' : l.quantity - l.receivedQuantity}</Td>
                  <Td>
                    {l.discrepancyReason ?? ''}
                    {l.resolution && <span className="block text-xs text-slate-500">{l.resolution.replace(/_/g, ' ')}</span>}
                  </Td>
                </tr>
              ))}
            </Table>
          )}
          {t.status === 'discrepancy' &&
            (can('pharmacy:stock_approve') ? (
              <ReconcileForm transfer={t} />
            ) : (
              <p className="text-sm text-amber-700">This transfer arrived short. Someone who approves stock must reconcile it.</p>
            ))}
          {t.reconciliationNote && <p className="text-sm text-slate-600">Reconciled: {t.reconciliationNote}</p>}
        </div>
      )}
    </Modal>
  );
}

function ReceiveForm({ transfer }: { transfer: Transfer }): ReactNode {
  const [lines, setLines] = useState(() => transfer.lines.map((l) => ({ lineId: l.id, receivedQuantity: String(l.quantity), discrepancyReason: '' })));
  const receive = usePharmacyWrite<Record<string, unknown>>('post', `/transfers/${transfer.id}/receive`);
  const problems = transfer.lines.map((l, i) => {
    const got = lines[i]!;
    if (!/^\d+$/.test(got.receivedQuantity)) return 'Enter what arrived, 0 if nothing';
    if (Number(got.receivedQuantity) > l.quantity) return 'More than was sent — record extra as found stock';
    if (Number(got.receivedQuantity) < l.quantity && !got.discrepancyReason.trim()) return 'Say why it is short';
    return null;
  });

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        receive.mutate({
          lines: lines.map((l) => ({ lineId: l.lineId, receivedQuantity: Number(l.receivedQuantity), discrepancyReason: l.discrepancyReason.trim() || null })),
        });
      }}
    >
      <CardHeader title="Record what arrived" subtitle="Every line, including zero. This can be done once." />
      {transfer.lines.map((l, i) => (
        <div key={l.id} className="rounded-lg border border-slate-200 p-3">
          <p className="text-sm font-medium">
            {l.productName} · batch {l.batchNumber} · exp {l.expiryDate} · <span className="tabular-nums">{l.quantity} sent</span>
          </p>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Input
              label="Received"
              type="number"
              min={0}
              max={l.quantity}
              step={1}
              value={lines[i]!.receivedQuantity}
              error={problems[i] ?? undefined}
              onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, receivedQuantity: e.target.value } : x)))}
            />
            {Number(lines[i]!.receivedQuantity) < l.quantity && (
              <div className="sm:col-span-2">
                <Input
                  label="Why short"
                  value={lines[i]!.discrepancyReason}
                  onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, discrepancyReason: e.target.value } : x)))}
                />
              </div>
            )}
          </div>
        </div>
      ))}
      <ErrorLine error={receive.error} />
      <div className="flex justify-end">
        <Button type="submit" loading={receive.isPending} disabled={problems.some(Boolean)}>
          Confirm receipt
        </Button>
      </div>
    </form>
  );
}

function ReconcileForm({ transfer }: { transfer: Transfer }): ReactNode {
  const short = transfer.lines.filter((l) => (l.receivedQuantity ?? 0) < l.quantity && !l.resolution);
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  const reconcile = usePharmacyWrite<Record<string, unknown>>('post', `/transfers/${transfer.id}/reconcile`);
  const complete = short.every((l) => choices[l.id]) && note.trim().length >= 3;

  return (
    <Card>
      <CardHeader title="Reconcile the shortfall" subtitle="Write it off as a loss, or put it back on the source's books" />
      <div className="space-y-3">
        {short.map((l) => (
          <Select
            key={l.id}
            label={`${l.productName} · batch ${l.batchNumber} — ${l.quantity - (l.receivedQuantity ?? 0)} short`}
            value={choices[l.id] ?? ''}
            onChange={(e) => setChoices({ ...choices, [l.id]: e.target.value })}
            placeholder="Decide"
            options={[
              { value: 'written_off', label: 'Write off — lost or damaged in transit' },
              { value: 'returned_to_source', label: 'Return to source — it never left' },
            ]}
          />
        ))}
        <Textarea label="Note" required value={note} onChange={(e) => setNote(e.target.value)} />
        <ErrorLine error={reconcile.error} />
        <div className="flex justify-end">
          <Button
            loading={reconcile.isPending}
            disabled={!complete}
            onClick={() => reconcile.mutate({ lines: short.map((l) => ({ lineId: l.id, resolution: choices[l.id] })), note })}
          >
            Reconcile
          </Button>
        </div>
      </div>
    </Card>
  );
}
